import React from 'react';
import { DatePicker } from '~/components/Form/DatePicker';
import moment from 'moment';
import { ValidationMessage } from '~/components/Form';
import { HistoricDocumentResults } from '~/components/Patient/Attestations/HistoricDocuments/HistoricDocumentResults';
import { Spinner } from 'react-bootstrap';
import { PatientContext, ServicesContext } from '~/services';

export interface HistoricDocumentType {
    bookmarkFileDataId: string;
    bookmarkDocumentName: string;
    bookmarkDocumentType: string;
    bookmarkDocumentDate: string;
}

interface HistoricDocumentsProps {
    icdCode: string;
}

export function HistoricDocuments({ icdCode }: HistoricDocumentsProps) {
    const services = React.useContext(ServicesContext);
    const patientContext = React.useContext(PatientContext);
    const initialLoad = React.useRef(true);
    const [from, setFrom] = React.useState<null | Date>(moment().subtract(3, 'years').toDate());
    const [to, setTo] = React.useState<null | Date>(new Date());
    const [valid, setValid] = React.useState(true);
    const [invalidDatesError, setInvalidDatesError] = React.useState('');
    const [showResults, setShowResults] = React.useState(false);
    const [results, setResults] = React.useState<HistoricDocumentType[]>([]);
    const [resultError, setResultError] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const minDate = moment().subtract(10, 'years').toDate();
    const maxDate = new Date();

    React.useEffect(() => {
        setValid(true);
        setInvalidDatesError('');
        if (to === null || from === null) {
            setValid(false);
        }
        if (moment(to).isBefore(moment(from), 'day')) {
            setValid(false);
            setInvalidDatesError('End Date must be after Start Date');
        }
    }, [from, to]);

    const search = React.useCallback(async () => {
        try {
            setLoading(true);
            setResultError(false);
            const data = await services.api.call(
                {
                    method: 'get',
                    url: `/wellmed/api/gmpi/${patientContext?.patientGmpi}/bookmark-documents`,
                    params: {
                        icd: icdCode,
                        fromDate: `${
                            from!.getMonth() + 1
                        }-${from!.getDate()}-${from!.getFullYear()}`,
                        toDate: `${to!.getMonth() + 1}-${to!.getDate()}-${to!.getFullYear()}`,
                    },
                },
                { handledErrors: true }
            );
            setResults(data.data.data.memberIcdHistoricalDocuments);
        } catch (e) {
            setResultError(true);
        } finally {
            setShowResults(true);
            setLoading(false);
        }
    }, [from, to, icdCode, patientContext?.patientGmpi, services.api]);

    React.useEffect(() => {
        if (initialLoad.current) {
            initialLoad.current = false;
            search();
        }
    }, [search]);

    return (
        <>
            <div className="row mb-3">
                <div className="col-sm-5 col-12 mb-sm-0 mb-2">
                    <span className="me-2">Start Date</span>
                    <DatePicker
                        id="docs-from"
                        name="docs-from"
                        value={from}
                        onChange={(val: null | Date) => setFrom(val)}
                        minDate={minDate}
                        maxDate={maxDate}
                    />
                </div>
                <div className="col-sm-5 col-12 mb-sm-0 mb-2">
                    <span className="me-2">End Date</span>
                    <DatePicker
                        id="docs-to"
                        name="docs-to"
                        value={to}
                        onChange={(val: null | Date) => setTo(val)}
                        minDate={minDate}
                        maxDate={maxDate}
                    />
                    <ValidationMessage
                        type="external"
                        rule={invalidDatesError !== ''}
                        showBeforeSubmit
                    >
                        {invalidDatesError}
                    </ValidationMessage>
                </div>
                <div className="col-2 text-end">
                    <button
                        type="button"
                        className="btn btn-primary"
                        disabled={!valid || loading}
                        onClick={search}
                    >
                        {loading ? (
                            <Spinner animation="border" size="sm" role="status" className="mx-2" />
                        ) : (
                            'Search'
                        )}
                    </button>
                </div>
            </div>
            <HistoricDocumentResults show={showResults} results={results} error={resultError} />
        </>
    );
}
